import type { ProFormColumnsType } from '@ant-design/pro-components';
import type { BtlApplication } from 'api/btlApplicationForm/types';
import { App } from 'antd';
import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { FormValidationContext } from 'hooks';
import { useBtlApplicationInProgressById } from 'queries';
import { isDipAcceptedOrLater } from '../constants';
import { makeFormReadonly } from '../makeFormReadonly';
import type { BaseFormSchemaProps } from '../types';
import { useBtlFormMappedRoutes } from '../useBtlFormMappedRoutes';
import { mapApplicationToFormValues } from './mapApplicationToFormValues';
import { submitFinancingAndPropertyForm } from './submitFinancingAndPropertyForm';

interface FinancingAndPropertySchemaProps extends BaseFormSchemaProps {
  isDipAccepted?: boolean;
  isDipAcceptedOrLater?: boolean;
}

interface UseFinancingAndPropertyFormParams {
  formName: string;
  getFormSchema: (schemaProps: FinancingAndPropertySchemaProps) => ProFormColumnsType[];
}

function useFinancingAndPropertyFormSchemaProps(): FinancingAndPropertySchemaProps & {
  data?: BtlApplication;
  isLoading: boolean;
} {
  const { formatMessage } = useIntl();
  const { id = '' } = useParams();
  const { data, isLoading } = useBtlApplicationInProgressById(id);
  const { isFormEditable } = useBtlFormMappedRoutes(data);

  const isDipAcceptedOrLaterStatus = isDipAcceptedOrLater(data?.status);

  return {
    formatMessage,
    data,
    isLoading,
    isFormEditable,
    isDipAccepted: data?.status === 'DIP_ACCEPTED',
    isDipAcceptedOrLater: isDipAcceptedOrLaterStatus,
  };
}

export function useFinancingAndPropertyForm({ formName, getFormSchema }: UseFinancingAndPropertyFormParams) {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { id = '' } = useParams();
  const schemaProps = useFinancingAndPropertyFormSchemaProps();
  const { data, isLoading, isFormEditable } = schemaProps;
  const propertyId = data?.applicationProperty?.property?.id;
  const { nextSection: section = '' } = useBtlFormMappedRoutes(data);
  const formValidationContext = useContext(FormValidationContext);

  if (!formValidationContext) {
    throw new Error('Financing and property forms must be used within a FormValidationProvider');
  }

  const { setIsSubmitting } = formValidationContext;

  if (isLoading || isFormEditable === undefined) {
    return { isLoading: true as const };
  }

  const formSchema = getFormSchema(schemaProps);
  const adjustedFormSchema = isFormEditable ? formSchema : makeFormReadonly(formSchema);

  return {
    isLoading: false as const,
    formProps: {
      columns: adjustedFormSchema,
      isFormEditable,
      name: formName,
      request: async () => mapApplicationToFormValues(data),
      onFinish: async (values: Record<string, unknown>) => {
        await submitFinancingAndPropertyForm({
          values,
          data,
          id,
          propertyId,
          nextSection: section,
          navigate,
          setIsSubmitting,
          message,
        });
      },
    },
  };
}
